import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, Slider, Space, Typography } from 'antd';
import { AimOutlined } from '@ant-design/icons';
import { heroPhotoLayerStyle } from '../../services/homepageService';

const { Text } = Typography;

export interface FocusPoint {
  x: number;
  y: number;
}

const CENTRE: FocusPoint = { x: 50, y: 50 };
/** Arrow keys move the focus point this far; with Shift, ten times as far. */
const KEY_STEP = 1;

const clampPercent = (value: number): number => Math.min(100, Math.max(0, Math.round(value)));

/** '30% 70%' → { x: 30, y: 70 }; anything unreadable is the centre. */
export const parseFocusPoint = (position: string | null | undefined): FocusPoint => {
  const match = /^(\d{1,3})% (\d{1,3})%$/.exec((position ?? '').trim());
  if (!match) return CENTRE;
  const x = Number(match[1]);
  const y = Number(match[2]);
  return x <= 100 && y <= 100 ? { x, y } : CENTRE;
};

export const formatFocusPoint = ({ x, y }: FocusPoint): string => `${x}% ${y}%`;

/** Zoom runs from 100% (the photo just fills the section) to 200%. */
const MIN_ZOOM = 100;
const MAX_ZOOM = 200;
const ZOOM_STEP = 5;

/** '150' → 150; anything unreadable or out of range is no zoom. */
export const parseZoom = (zoom: string | null | undefined): number => {
  const text = (zoom ?? '').trim();
  const value = /^\d{1,3}$/.test(text) ? Number(text) : MIN_ZOOM;
  return value >= MIN_ZOOM && value <= MAX_ZOOM ? value : MIN_ZOOM;
};

/**
 * The top section's shape (width ÷ height). It is the full width and at least
 * 60% of the window's height, but never shorter than its headline, text and
 * button (about 470px on a computer). So on a 16:9 computer screen it is about
 * 3:1, and on a phone, where the headline wraps onto more lines, about 3:5.
 */
const MIN_COMPUTER_SECTION_HEIGHT = 470;
export const COMPUTER_SECTION_RATIO = 1920 / Math.max(1080 * 0.6, MIN_COMPUTER_SECTION_HEIGHT);
export const PHONE_SECTION_RATIO = 3 / 5;

/**
 * The top section's shape in a computer window of the given size. Browser bars
 * make a window shorter than its screen, which makes the section wider and
 * leaves a wide photo much less room to move (issue #162), so the computer
 * preview follows the admin's own window rather than an ideal screen. A
 * window too narrow to be a computer's falls back to a 16:9 screen.
 */
export const computerSectionRatio = (width: number, height: number): number => {
  const ratio = width / Math.max(height * 0.6, MIN_COMPUTER_SECTION_HEIGHT);
  return ratio >= 1.6 ? ratio : COMPUTER_SECTION_RATIO;
};

/**
 * Which ways the focus point can shift the photo in a section of the given
 * shape (issue #162). With `cover` the photo exactly fills the section one way
 * and only overflows the other, so a photo wider than the section can pan left
 * and right but never up and down, and a taller one the reverse. Zooming in
 * makes it overflow both ways.
 */
export const movableDirections = (photoRatio: number, sectionRatio: number, zoom = MIN_ZOOM) => {
  const zoomed = zoom > MIN_ZOOM;
  return {
    across: zoomed || photoRatio > sectionRatio * 1.01,
    upDown: zoomed || photoRatio < sectionRatio / 1.01,
  };
};

/**
 * Tells staff which ways moving the focus point changes anything for this
 * photo, and that zooming in frees the rest, so a drag that cannot move it
 * doesn't look like a broken editor.
 */
export const framingHint = (photoRatio: number, computerRatio: number, zoom: number): string => {
  if (zoom > MIN_ZOOM) {
    return 'Zoomed in, so moving the point shifts the photo every way. The further you zoom in, the softer the photo looks.';
  }
  const computer = movableDirections(photoRatio, computerRatio);
  const phone = movableDirections(photoRatio, PHONE_SECTION_RATIO);
  if (!computer.upDown && !phone.upDown) {
    return 'This photo is wider than the top section, so it fills the section from top to bottom and moving the point up or down changes nothing. Zoom in to move it up and down as well.';
  }
  if (!computer.across && !phone.across) {
    return 'This photo is taller than the top section, so it fills the section from side to side and moving the point left or right changes nothing. Zoom in to move it left and right as well.';
  }
  return 'On a computer only moving the point up or down changes which part shows; on a phone only left and right does. Zoom in to move it every way.';
};

interface HeroBackgroundEditorProps {
  open: boolean;
  /** The photo being edited: a local preview of a new file, or the live one. */
  imageUrl: string;
  /** Where the focus point starts, as 'X% Y%'. */
  initialPosition: string;
  /** The zoom it starts at, in percent. */
  initialZoom: number;
  headline: string;
  subtext: string;
  saving: boolean;
  onCancel: () => void;
  onSave: (position: string, zoom: number) => void;
}

interface PreviewProps {
  label: string;
  imageUrl: string;
  position: string;
  zoom: number;
  headline: string;
  subtext: string;
  width: number | string;
  /** The section's shape, width ÷ height. */
  ratio: number;
  headlineSize: number;
}

/**
 * The top section as customers will see it at one screen shape: the photo
 * darkened and positioned exactly as the homepage does it, under the white
 * headline.
 */
const SectionPreview: React.FC<PreviewProps> = ({
  label,
  imageUrl,
  position,
  zoom,
  headline,
  subtext,
  width,
  ratio,
  headlineSize,
}) => (
  <div>
    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
      {label}
    </Text>
    <div
      role="img"
      aria-label={`${label} preview`}
      data-position={position}
      data-zoom={zoom}
      style={{
        position: 'relative',
        width,
        maxWidth: '100%',
        aspectRatio: String(ratio),
        borderRadius: 8,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: 16,
        color: '#fff',
      }}
    >
      <div aria-hidden style={heroPhotoLayerStyle(imageUrl, position, zoom)} />
      <div style={{ position: 'relative', fontSize: headlineSize, fontWeight: 700, lineHeight: 1.2 }}>{headline}</div>
      <div
        style={{
          position: 'relative',
          fontSize: Math.max(10, headlineSize * 0.45),
          marginTop: 6,
          opacity: 0.9,
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {subtext}
      </div>
    </div>
  </div>
);

/**
 * Preview and frame the homepage's top-section background photo before it
 * goes live (issue #154).
 *
 * The section is a wide strip on a computer and a tall block on a phone, so a
 * single crop cannot suit both. Instead staff mark the part of the photo that
 * matters, and the homepage keeps that point in view at every screen shape.
 * Nothing is saved until "Save background".
 */
const HeroBackgroundEditor: React.FC<HeroBackgroundEditorProps> = ({
  open,
  imageUrl,
  initialPosition,
  initialZoom,
  headline,
  subtext,
  saving,
  onCancel,
  onSave,
}) => {
  const [focus, setFocus] = useState<FocusPoint>(() => parseFocusPoint(initialPosition));
  const [zoom, setZoom] = useState(initialZoom);
  // The photo's own shape, width ÷ height, once it has loaded. Kept with its
  // URL so a different photo's shape is unknown until that one loads.
  const [loadedPhoto, setLoadedPhoto] = useState<{ url: string; ratio: number } | null>(null);
  const photoRatio = loadedPhoto?.url === imageUrl ? loadedPhoto.ratio : null;
  const photoRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  // Each time the editor opens it starts from the saved framing.
  useEffect(() => {
    if (open) {
      setFocus(parseFocusPoint(initialPosition));
      setZoom(initialZoom);
    }
  }, [open, initialPosition, initialZoom]);

  const position = formatFocusPoint(focus);
  const computerRatio = computerSectionRatio(window.innerWidth, window.innerHeight);

  const moveTo = (clientX: number, clientY: number) => {
    const rect = photoRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return;
    setFocus({
      x: clampPercent(((clientX - rect.left) / rect.width) * 100),
      y: clampPercent(((clientY - rect.top) / rect.height) * 100),
    });
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    moveTo(e.clientX, e.clientY);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragging.current) moveTo(e.clientX, e.clientY);
  };

  const handlePointerUp = () => {
    dragging.current = false;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? KEY_STEP * 10 : KEY_STEP;
    const moves: Record<string, FocusPoint> = {
      ArrowLeft: { x: focus.x - step, y: focus.y },
      ArrowRight: { x: focus.x + step, y: focus.y },
      ArrowUp: { x: focus.x, y: focus.y - step },
      ArrowDown: { x: focus.x, y: focus.y + step },
    };
    const next = moves[e.key];
    if (!next) return;
    e.preventDefault();
    setFocus({ x: clampPercent(next.x), y: clampPercent(next.y) });
  };

  return (
    <Modal
      title="Preview the top section background"
      open={open}
      onCancel={onCancel}
      width={820}
      destroyOnClose
      maskClosable={!saving}
      footer={[
        <Button key="centre" icon={<AimOutlined />} onClick={() => setFocus(CENTRE)} disabled={saving}>
          Centre
        </Button>,
        <Button key="cancel" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={() => onSave(position, zoom)}>
          Save background
        </Button>,
      ]}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text>
          Tap or drag on the photo to mark the part that must always show, and zoom in to fill the section
          with less of it. The previews show how the top section looks on a computer the shape of this
          window and on a phone. Nothing changes on the website until you save.
        </Text>
        {photoRatio !== null && (
          <Alert type="info" showIcon message={framingHint(photoRatio, computerRatio, zoom)} />
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 300px', minWidth: 0 }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
              Your photo
            </Text>
            <div
              ref={photoRef}
              role="slider"
              tabIndex={0}
              aria-label="Focus point"
              aria-valuetext={`${focus.x}% across, ${focus.y}% down`}
              aria-valuenow={focus.x}
              aria-valuemin={0}
              aria-valuemax={100}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onKeyDown={handleKeyDown}
              style={{
                position: 'relative',
                display: 'inline-block',
                maxWidth: '100%',
                cursor: 'crosshair',
                touchAction: 'none',
                userSelect: 'none',
                lineHeight: 0,
              }}
            >
              <img
                src={imageUrl}
                alt="Background photo"
                draggable={false}
                onLoad={(e) => {
                  const { naturalWidth, naturalHeight } = e.currentTarget;
                  if (naturalWidth && naturalHeight) {
                    setLoadedPhoto({ url: imageUrl, ratio: naturalWidth / naturalHeight });
                  }
                }}
                style={{ display: 'block', maxWidth: '100%', maxHeight: 320, borderRadius: 8 }}
              />
              <span
                data-testid="focus-marker"
                aria-hidden
                style={{
                  position: 'absolute',
                  left: `${focus.x}%`,
                  top: `${focus.y}%`,
                  width: 28,
                  height: 28,
                  marginLeft: -14,
                  marginTop: -14,
                  borderRadius: '50%',
                  border: '3px solid #fff',
                  boxShadow: '0 0 0 2px var(--dasoon-primary), 0 2px 6px rgba(0, 0, 0, 0.5)',
                  pointerEvents: 'none',
                }}
              />
            </div>

            <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
              Zoom: {zoom}%
            </Text>
            <Slider
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={ZOOM_STEP}
              value={zoom}
              onChange={(value: number) => setZoom(value)}
              disabled={saving}
              ariaLabelForHandle="Zoom"
              tooltip={{ formatter: (value) => `${value}%` }}
            />
          </div>

          <div style={{ flex: '1 1 340px', minWidth: 0 }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <SectionPreview
                label="Computer"
                imageUrl={imageUrl}
                position={position}
                zoom={zoom}
                headline={headline}
                subtext={subtext}
                width="100%"
                ratio={computerRatio}
                headlineSize={20}
              />
              <SectionPreview
                label="Phone"
                imageUrl={imageUrl}
                position={position}
                zoom={zoom}
                headline={headline}
                subtext={subtext}
                width={150}
                ratio={PHONE_SECTION_RATIO}
                headlineSize={15}
              />
            </Space>
          </div>
        </div>
      </Space>
    </Modal>
  );
};

export default HeroBackgroundEditor;
